import React, { useState, useEffect, useCallback, useMemo, useRef, Fragment } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Button,
  Autocomplete,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Link,
  TextField,
  Snackbar,
  CircularProgress
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Print as PrintIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { get, authPut, authPost, invalidateCache } from '../../services/apiService';
import { isAuthenticated, isAdmin, isManager } from '../../services/authService';
import Loading from '../common/Loading';

const ADO_WORK_ITEM_URL = 'https://tr-legal.visualstudio.com/HighQ%20Tech/_workitems/edit/';

const COLORS = {
  green: '#16a34a',
  orange: '#d97706',
  red: '#dc2626',
  gray: '#94a3b8',
  track: '#e2e8f0',
  headerBg: '#f1f5f9',
  border: '#e2e8f0'
};

// Column groups for the detail table: key into row.counts, header label, accent colour, tint for non-zero cells
const AUTOMATION_COLUMNS = [
  { key: 'automatedUI', label: 'Automated-UI', color: '#2563eb', tint: '#e0eafc' },
  { key: 'automatedAPI', label: 'Automated-API', color: '#0d9488', tint: '#dcf3ef' },
  { key: 'unit', label: 'Unit framework', color: '#7c3aed', tint: '#ede5fd' },
  { key: 'integration', label: 'Integration framework', color: '#0284c7', tint: '#dcf0fa' },
  { key: 'otherAutomated', label: 'Other automated', color: '#64748b', tint: '#eef1f5' },
  { key: 'manual', label: 'Manual', color: '#9333ea', tint: '#f3e8ff' }
];
const REPO_COLUMNS = [
  { key: 'idFound', label: 'ID found', color: '#16a34a', tint: '#dcf5e4' },
  { key: 'adoAutomatedIdMissing', label: 'ADO automated, ID missing', color: '#dc2626', tint: '#fde4e4' },
  { key: 'idFoundAdoManual', label: 'ID found, ADO manual', color: '#d97706', tint: '#fdf0dc' }
];
const OUTCOME_COLUMNS = [
  { key: 'passed', label: 'Passed', color: '#16a34a', tint: '#dcf5e4' },
  { key: 'failed', label: 'Failed', color: '#dc2626', tint: '#fde4e4' },
  { key: 'notRun', label: 'Not run', color: '#d97706', tint: '#fdf0dc' },
  { key: 'notInTP', label: 'Not found in TP', color: '#7c3aed', tint: '#ede5fd' }
];
const DECISION_COLORS = { Go: COLORS.green, Risk: COLORS.orange, 'No-Go': COLORS.red };
const OUTCOME_LABELS = { passed: 'Passed', failed: 'Failed', notRun: 'Not run', notInTP: 'Not in TP' };
const MAX_TEST_PLANS = 2;
// Short labels for the compact count cells in the feature table
const SHORT_LABELS = {
  automatedUI: 'UI',
  automatedAPI: 'API',
  unit: 'Unit',
  integration: 'Integration',
  otherAutomated: 'Other',
  manual: 'Manual',
  idFound: '✓ found',
  adoAutomatedIdMissing: '✗ missing',
  idFoundAdoManual: '⚠ ADO manual'
};
// Status of a test method added by a PR, looked up in the PR's own test reports
const NEW_TEST_STATUSES = [
  { key: 'passed', label: 'Ran · passed', color: COLORS.green },
  { key: 'failed', label: 'Ran · failed', color: COLORS.red },
  { key: 'skipped', label: 'Ran · skipped', color: COLORS.gray },
  { key: 'notRun', label: 'Not run', color: COLORS.orange },
  { key: 'unknown', label: "Can't verify", color: '#64748b' }
];
const PR_TESTS_URL = '/api/fix-versions/release-status/pr-tests';
const PR_CHUNK_SIZE = 8;
const PR_PARALLEL_REQUESTS = 2;
const MAX_NEW_TESTS_SHOWN = 40;

const prKey = (pr) => `${pr.repo}#${pr.number}`;

/**
 * Roll up PR unit-test data for a set of linked PRs (deduplicated), using whatever has loaded so far
 */
const prStatsOf = (pullRequests, prTests, requested = new Set()) => {
  const unique = [...new Map(pullRequests.map(pr => [pr.repo ? prKey(pr) : `${pr.repoId}#${pr.number}`, pr])).values()];
  const resolved = unique.filter(pr => pr.repo);
  const loaded = resolved.map(pr => prTests[prKey(pr)]).filter(Boolean);
  const notLoaded = resolved.filter(pr => !prTests[prKey(pr)]);
  const ok = loaded.filter(r => !r.error);
  const newTests = Object.fromEntries(['total', ...NEW_TEST_STATUSES.map(s => s.key)].map(k => [k, 0]));
  ok.forEach(r => r.newTests.forEach(t => {
    newTests.total++;
    newTests[t.status] = (newTests[t.status] || 0) + 1;
  }));
  return {
    prs: unique.length,
    unresolved: unique.length - resolved.length,
    pending: notLoaded.filter(pr => requested.has(prKey(pr))).length,
    notLoaded: notLoaded.filter(pr => !requested.has(prKey(pr))).length,
    errors: loaded.length - ok.length,
    merged: ok.filter(r => r.state === 'merged').length,
    withTestChanges: ok.filter(r => r.testFilesChanged > 0).length,
    newTests,
    failingSuitePrs: ok.filter(r => r.suites.some(s => s.conclusion === 'failure')).length,
    noSuitePrs: ok.filter(r => r.suites.length === 0).length
  };
};

const NewTestCaption = ({ stats }) => (
  <>
    {NEW_TEST_STATUSES.filter(s => stats[s.key] > 0).map((s, i) => (
      <Fragment key={s.key}>
        {i > 0 && ' · '}
        <Box component="span" sx={{ color: s.color }}>{stats[s.key]} {s.label.toLowerCase()}</Box>
      </Fragment>
    ))}
  </>
);

const padPlans = (plans = []) => Array.from({ length: MAX_TEST_PLANS }, (_, i) => ({
  planId: plans[i]?.planId || '',
  suiteId: plans[i]?.suiteId || ''
}));

const repoCheckLabel = (test) => {
  if (test.repoCheck === 'found') return test.repoExpected === 'e2e/releases' ? 'Found in e2e/releases' : 'Found in repo';
  if (test.repoCheck === 'missing') return test.repoExpected === 'e2e/releases' ? 'Missing from e2e/releases' : 'Missing from repo';
  if (test.repoCheck === 'foundManual') return 'Found, but ADO manual';
  return '—';
};
const REPO_CHECK_COLORS = { found: '#16a34a', missing: '#dc2626', foundManual: '#d97706' };

const pct = (num, den) => (den > 0 ? Math.round((num / den) * 100) : 0);

const barColor = (value, den) => {
  if (!den) return COLORS.gray;
  if (value >= 90) return COLORS.green;
  if (value >= 60) return COLORS.orange;
  return COLORS.red;
};

const headCellSx = {
  backgroundColor: COLORS.headerBg,
  fontWeight: 700,
  fontSize: '0.75rem',
  lineHeight: 1.3,
  verticalAlign: 'top',
  borderRight: `1px solid ${COLORS.border}`,
  px: 1,
  py: 1
};
const cellSx = {
  fontSize: '0.8rem',
  verticalAlign: 'top',
  borderRight: `1px solid ${COLORS.border}`,
  px: 1,
  py: 1
};

/**
 * Horizontal progress bar with the percentage on the right and an optional caption below
 */
const ProgressCell = ({ num, den, caption, minWidth = 140 }) => {
  const value = pct(num, den);
  const color = barColor(value, den);
  return (
    <Box sx={{ minWidth }}>
      <Box display="flex" alignItems="center" gap={1}>
        <Box sx={{ flex: 1, height: 10, borderRadius: 5, backgroundColor: COLORS.track, overflow: 'hidden' }}>
          <Box sx={{ width: `${value}%`, height: '100%', backgroundColor: color, minWidth: value > 0 ? 4 : 0 }} />
        </Box>
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color, minWidth: 34, textAlign: 'right' }}>
          {value}%
        </Typography>
      </Box>
      {caption && (
        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.25 }}>{caption}</Typography>
      )}
    </Box>
  );
};

const ExecutionCaption = ({ counts }) => (
  <>
    <Box component="span" sx={{ color: COLORS.green }}>{counts.passed} passed</Box>
    {' · '}
    <Box component="span" sx={{ color: COLORS.red }}>{counts.failed} failed</Box>
    {' · '}
    <Box component="span" sx={{ color: COLORS.orange }}>{counts.notRun} not run</Box>
    {' · '}
    <Box component="span" sx={{ color: '#7c3aed' }}>{counts.notInTP} not in TP</Box>
  </>
);

/**
 * Non-zero counts of a column group as small tinted chips (full label on hover)
 */
const CountChips = ({ counts, columns }) => {
  const shown = columns.filter(col => counts[col.key] > 0);
  if (shown.length === 0) return <Typography sx={{ fontSize: '0.8rem', color: COLORS.gray }}>—</Typography>;
  return (
    <Box display="flex" flexWrap="wrap" gap={0.5}>
      {shown.map(col => (
        <Tooltip key={col.key} title={col.label}>
          <Box
            component="span"
            sx={{ backgroundColor: col.tint, color: col.color, fontWeight: 700, fontSize: '0.72rem', px: 0.75, py: 0.1, borderRadius: 1, whiteSpace: 'nowrap' }}
          >
            {SHORT_LABELS[col.key] || col.label} {counts[col.key]}
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
};

const emptyTotals = () => ({
  features: 0,
  completion: { done: 0, total: 0 },
  scenarios: { covered: 0, total: 0 },
  linkedCount: 0,
  newCount: 0,
  newAutomatedCount: 0,
  counts: Object.fromEntries([...AUTOMATION_COLUMNS, ...REPO_COLUMNS, ...OUTCOME_COLUMNS].map(c => [c.key, 0])),
  decisions: { Go: 0, Risk: 0, 'No-Go': 0, undecided: 0 }
});

/**
 * Same roll-up as the server's summary rows, so area filtering and unsaved decisions stay in sync client-side
 */
const summarizeRows = (rows, areaPath) => rows.reduce((summary, row) => {
  summary.features++;
  summary.completion.done += row.completion.done;
  summary.completion.total += row.completion.total;
  summary.scenarios.covered += row.scenarios.covered;
  summary.scenarios.total += row.scenarios.total;
  summary.linkedCount += row.linkedCount;
  summary.newCount += row.newCount;
  summary.newAutomatedCount += row.newAutomatedCount;
  Object.keys(summary.counts).forEach(key => { summary.counts[key] += row.counts[key] || 0; });
  if (row.decision) summary.decisions[row.decision]++;
  else summary.decisions.undecided++;
  return summary;
}, { ...emptyTotals(), areaPath });

/**
 * Release Status tab - Feature/Epic status for a release: completion, linked tests,
 * automation status in ADO, presence in the e2e repo, test plan outcome and Go / Risk / No-Go decisions
 */
const ReleaseStatusTab = ({ fixVersion: propFixVersion, fixVersions = [] }) => {
  const [selectedFixVersion, setSelectedFixVersion] = useState(propFixVersion || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [expandedTests, setExpandedTests] = useState(new Set());
  const [snackbar, setSnackbar] = useState('');
  const [allVersions, setAllVersions] = useState([]);
  const [areaFilter, setAreaFilter] = useState([]);
  // Unsaved decision/comment edits, keyed by feature ID: { decision?, comment? }
  const [pendingChanges, setPendingChanges] = useState({});
  const [saving, setSaving] = useState(false);
  // Test plan inputs (plan ID + parent suite ID, max two); filled from the plans the report used
  const [planInputs, setPlanInputs] = useState(padPlans());
  const [savingPlans, setSavingPlans] = useState(false);
  // PR unit-test data keyed by "<repo>#<number>", loaded after the report; kept across refreshes
  const [prTests, setPrTests] = useState({});
  const [prProgress, setPrProgress] = useState({ done: 0, total: 0, error: null });

  const canSave = isAuthenticated() && (isAdmin() || isManager());
  const pendingCount = Object.keys(pendingChanges).length;
  const versionOptions = allVersions.length > 0 ? allVersions : fixVersions;

  useEffect(() => {
    if (propFixVersion) setSelectedFixVersion(propFixVersion);
  }, [propFixVersion]);

  // Every fix version known to the portal (FixVersion records + versions used on work items)
  useEffect(() => {
    get('/api/fix-versions/release-status/versions')
      .then(response => {
        if (response && response.success && Array.isArray(response.data)) setAllVersions(response.data);
      })
      .catch(err => console.error('Error fetching release versions:', err));
  }, []);

  // testPlans: [{ planId, suiteId }] to use instead of the plans saved for the release
  const fetchReport = useCallback(async (refreshRepo = false, testPlans = null) => {
    if (!selectedFixVersion) return;
    try {
      setLoading(true);
      setError(null);
      const url = `/api/fix-versions/${selectedFixVersion}/release-status`;
      if (refreshRepo) invalidateCache(url);
      const params = {};
      if (refreshRepo) params.refreshRepo = 'true';
      if (testPlans) params.testPlans = testPlans.map(p => `${p.planId}:${p.suiteId}`).join(',');
      const useCache = !refreshRepo && !testPlans;
      const response = await get(url, Object.keys(params).length > 0 ? params : null, 'json', { useCache });
      if (response && response.success && response.data) {
        setReport(response.data);
        setPlanInputs(padPlans(response.data.testPlans?.configured));
        setExpandedTests(new Set());
        setPendingChanges({});
        // Keep only area filters that still exist in this release
        const areas = new Set(response.data.features.map(f => f.areaPath));
        setAreaFilter(prev => prev.filter(a => areas.has(a)));
      } else {
        setError('Failed to load release status.');
      }
    } catch (err) {
      console.error('Error fetching release status:', err);
      setError(`Failed to load release status: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [selectedFixVersion]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // PR unit-test details are slow and use GitHub API quota (a release can link 1,000+ PRs), so they load on demand:
  // when a feature's details are opened, or for every shown feature from the header button
  const requestedPrs = useRef(new Set());
  const prGeneration = useRef(0);
  const reportRef = useRef(null);

  useEffect(() => {
    prGeneration.current++;
    setPrProgress({ done: 0, total: 0, error: null });
  }, [selectedFixVersion]);

  const loadPrTests = useCallback((pullRequests) => {
    const refs = [...new Map(pullRequests.filter(pr => pr.repo && !requestedPrs.current.has(prKey(pr)) && !prTests[prKey(pr)])
      .map(pr => [prKey(pr), { repo: pr.repo, number: pr.number }])).values()];
    if (refs.length === 0) return;
    refs.forEach(pr => requestedPrs.current.add(prKey(pr)));
    const generation = prGeneration.current;
    setPrProgress(prev => ({ ...prev, total: prev.total + refs.length, error: null }));

    const chunks = [];
    for (let i = 0; i < refs.length; i += PR_CHUNK_SIZE) chunks.push(refs.slice(i, i + PR_CHUNK_SIZE));
    let next = 0;
    const worker = async () => {
      while (next < chunks.length) {
        const chunk = chunks[next++];
        try {
          const response = await authPost(PR_TESTS_URL, { prs: chunk });
          if (response?.success) setPrTests(prev => ({ ...prev, ...response.data }));
          else throw new Error(response?.message || 'request failed');
        } catch (err) {
          // Allow a retry later
          chunk.forEach(pr => requestedPrs.current.delete(prKey(pr)));
          if (generation === prGeneration.current) setPrProgress(prev => ({ ...prev, error: err.message }));
        }
        if (generation === prGeneration.current) {
          setPrProgress(prev => ({ ...prev, done: Math.min(prev.done + chunk.length, prev.total) }));
        }
      }
    };
    Array.from({ length: PR_PARALLEL_REQUESTS }, worker);
  }, [prTests]);

  const toggleTests = (feature) => {
    if (!expandedTests.has(feature.id) && report?.pullRequestsAvailable) loadPrTests(feature.pullRequests || []);
    setExpandedTests(prev => {
      const next = new Set(prev);
      if (next.has(feature.id)) next.delete(feature.id);
      else next.add(feature.id);
      return next;
    });
  };

  // Record an edit; drop it again if it matches what is saved
  const editFeature = (feature, field, value) => {
    setPendingChanges(prev => {
      const next = { ...prev };
      const change = { ...(next[feature.id] || {}), [field]: value };
      if (change[field] === feature[field]) delete change[field];
      if (Object.keys(change).length === 0) delete next[feature.id];
      else next[feature.id] = change;
      return next;
    });
  };

  const saveChanges = async () => {
    if (!canSave) {
      setSnackbar('Log in as an admin, dev manager or QA manager to save decisions.');
      return;
    }
    setSaving(true);
    const entries = Object.entries(pendingChanges);
    const results = await Promise.allSettled(entries.map(([featureId, changes]) =>
      authPut(`/api/fix-versions/${selectedFixVersion}/release-status/decisions/${featureId}`, changes)
    ));
    const savedIds = new Set(entries.filter((_, i) => results[i].status === 'fulfilled').map(([id]) => id));
    const failed = results.filter(r => r.status === 'rejected');

    setReport(prev => ({
      ...prev,
      features: prev.features.map(f => (savedIds.has(f.id) ? { ...f, ...pendingChanges[f.id] } : f))
    }));
    setPendingChanges(prev => Object.fromEntries(Object.entries(prev).filter(([id]) => !savedIds.has(id))));
    invalidateCache(`/api/fix-versions/${selectedFixVersion}/release-status`);
    setSaving(false);
    setSnackbar(failed.length > 0
      ? `Saved ${savedIds.size} of ${entries.length}. Could not save: ${failed[0].reason?.message || 'unknown error'}`
      : `Saved ${savedIds.size} change${savedIds.size === 1 ? '' : 's'}.`);
  };

  // Filled-in test plan inputs, or an error message if a row is half filled / not numeric
  const readPlanInputs = () => {
    const plans = planInputs
      .map(p => ({ planId: p.planId.trim(), suiteId: p.suiteId.trim() }))
      .filter(p => p.planId || p.suiteId);
    const invalid = plans.find(p => !/^\d+$/.test(p.planId) || !/^\d+$/.test(p.suiteId));
    return invalid ? { error: 'Each test plan needs a numeric plan ID and parent suite ID.' } : { plans };
  };

  const applyPlans = () => {
    const { plans, error: inputError } = readPlanInputs();
    if (inputError) {
      setSnackbar(inputError);
      return;
    }
    fetchReport(false, plans);
  };

  const savePlans = async () => {
    const { plans, error: inputError } = readPlanInputs();
    if (inputError) {
      setSnackbar(inputError);
      return;
    }
    if (!canSave) {
      setSnackbar('Log in as an admin, dev manager or QA manager to save test plans for the release.');
      return;
    }
    setSavingPlans(true);
    try {
      await authPut(`/api/fix-versions/${selectedFixVersion}/release-status/config`, { testPlans: plans });
      invalidateCache(`/api/fix-versions/${selectedFixVersion}/release-status`);
      setSnackbar(`Saved test plans for ${selectedFixVersion}.`);
      fetchReport(false, plans);
    } catch (err) {
      setSnackbar(`Could not save test plans: ${err.message}`);
    } finally {
      setSavingPlans(false);
    }
  };

  const updatePlanInput = (index, field, value) => {
    setPlanInputs(prev => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  };

  // Rows with unsaved edits applied, narrowed by the area path filter; summaries recomputed to match
  const view = useMemo(() => {
    if (!report) return null;
    const allFeatures = report.features.map(f => (pendingChanges[f.id] ? { ...f, ...pendingChanges[f.id] } : f));
    const features = areaFilter.length > 0 ? allFeatures.filter(f => areaFilter.includes(f.areaPath)) : allFeatures;
    const areas = [...new Set(features.map(f => f.areaPath))].sort();
    const withPrStats = (rows, label) => ({
      ...summarizeRows(rows, label),
      prStats: prStatsOf(rows.flatMap(f => f.pullRequests || []), prTests, requestedPrs.current)
    });
    return {
      features,
      areaOptions: [...new Set(allFeatures.map(f => f.areaPath))].sort(),
      summaryByArea: areas.map(area => withPrStats(features.filter(f => f.areaPath === area), area)),
      total: withPrStats(features, 'Total')
    };
    // prProgress changes whenever PRs are requested, so the requested set is current here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, pendingChanges, areaFilter, prTests, prProgress]);

  const renderDecisionSummary = () => {
    const { decisions } = view.total;
    const tiles = [
      { label: 'Go', value: decisions.Go, color: COLORS.green },
      { label: 'Risk', value: decisions.Risk, color: COLORS.orange },
      { label: 'No-Go', value: decisions['No-Go'], color: COLORS.red },
      { label: 'Not decided', value: decisions.undecided, color: '#64748b' },
      { label: 'Features', value: view.features.length, color: '#0f172a' }
    ];
    return (
      <Box display="flex" gap={1.5} flexWrap="wrap" mt={1.5} sx={{ breakInside: 'avoid' }}>
        {tiles.map(tile => (
          <Box
            key={tile.label}
            sx={{
              minWidth: 110,
              px: 2,
              py: 1,
              border: `1px solid ${COLORS.border}`,
              borderTop: `3px solid ${tile.color}`,
              borderRadius: 1,
              backgroundColor: '#fff'
            }}
          >
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontWeight: 600 }}>{tile.label}</Typography>
            <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: tile.color, lineHeight: 1.2 }}>{tile.value}</Typography>
          </Box>
        ))}
      </Box>
    );
  };

  // Print only the report: copy it with the page's styles into a blank window, so the sidebar,
  // tabs and controls are left out
  const printReport = () => {
    const node = reportRef.current;
    if (!node) return;
    const win = window.open('', '_blank');
    if (!win) {
      setSnackbar('Allow pop-ups for this site to print the report.');
      return;
    }
    const styles = Array.from(document.styleSheets).map(sheet => {
      try {
        return `<style>${Array.from(sheet.cssRules).map(rule => rule.cssText).join('\n')}</style>`;
      } catch (e) {
        return sheet.href ? `<link rel="stylesheet" href="${sheet.href}">` : '';
      }
    }).join('\n');
    // Typed values (comments) live on the element, not in its HTML, so copy them onto the clone
    const clone = node.cloneNode(true);
    const sourceFields = node.querySelectorAll('input, textarea');
    clone.querySelectorAll('input, textarea').forEach((field, i) => {
      const value = sourceFields[i]?.value || '';
      if (field.tagName === 'TEXTAREA') field.textContent = value;
      else field.setAttribute('value', value);
    });
    clone.style.opacity = '1';
    const title = `Release Status ${String(report.fixVersion).replace(/[<>&"]/g, '')}`;
    win.document.open();
    win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${title}</title>${styles}
      <style>
        @page { size: A4 landscape; margin: 10mm; }
        body { margin: 0; padding: 12px; background: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .MuiTableContainer-root { overflow: visible !important; max-height: none !important; }
        tr { break-inside: avoid; }
      </style></head><body>${clone.outerHTML}</body></html>`);
    win.document.close();
    win.onafterprint = () => win.close();
    setTimeout(() => { win.focus(); win.print(); }, 300);
  };

  const renderHeader = () => {
    const { repo, testPlans, dataSource } = report;
    const rowsLabel = areaFilter.length > 0
      ? `${view.features.length} of ${report.features.length} rows (filtered by area path)`
      : `${report.features.length} rows`;
    const scopes = testPlans?.scopes || [];
    const shownPrStats = view.total.prStats;
    return (
      <Box mb={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Feature/Epic Status — {report.fixVersion}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {rowsLabel} · Generated {new Date(report.generatedAt).toLocaleString()}
        </Typography>
        {renderDecisionSummary()}
        {dataSource?.source === 'portal' && (
          <Alert severity="warning" sx={{ mt: 1, py: 0, fontSize: '0.8rem' }}>
            Azure DevOps could not be reached ({dataSource.error}). Links come from the portal&apos;s imported data instead,
            which has no acceptance criteria or Related links — scenarios use PBI/Bug titles, and test plan outcomes are unavailable.
          </Alert>
        )}
        <Box sx={{ borderTop: `1px solid ${COLORS.border}`, mt: 1, pt: 1 }}>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            Fix Version <strong>{report.fixVersion}</strong> · {rowsLabel} · Test Plans:{' '}
            {scopes.length > 0 ? scopes.map((scope, i) => (
              <Fragment key={`${scope.planId}/${scope.suiteId}`}>
                {i > 0 && ', '}
                <strong>#{scope.planId}</strong> under suite #{scope.suiteId}{' '}
                {scope.error ? (
                  <Box component="span" sx={{ color: COLORS.red }}>({scope.error})</Box>
                ) : (
                  `(${scope.suiteCount} suite${scope.suiteCount === 1 ? '' : 's'}, ${scope.testCaseCount} test cases)`
                )}
              </Fragment>
            )) : (
              <Box component="span" sx={{ color: COLORS.orange }}>none set — enter up to two test plans above to see outcomes</Box>
            )}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            {repo && !repo.error ? (
              <>
                Repo check in {repo.repo}: {repo.filesScanned} files, {repo.distinctNumbers} distinct numbers (of which{' '}
                <strong>{repo.releaseRoot}</strong>: {repo.releaseFiles} files, {repo.releaseNumbers} numbers).
              </>
            ) : (
              <Box component="span" sx={{ color: COLORS.red }}>
                Repo check unavailable{repo?.error ? `: ${repo.error}` : ''} — In repo columns are not counted.
              </Box>
            )}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            {!report.pullRequestsAvailable ? (
              <Box component="span" sx={{ color: COLORS.orange }}>PR unit tests unavailable — PR links are only read from Azure DevOps.</Box>
            ) : shownPrStats.prs === 0 ? (
              'PR unit tests: no GitHub PRs are linked to these features.'
            ) : (
              <>
                PR unit tests: {shownPrStats.prs} PRs linked to the {view.features.length} features shown
                {shownPrStats.unresolved > 0 && ` (${shownPrStats.unresolved} in repos missing from the portal's repo list)`}.{' '}
                {prProgress.done < prProgress.total && (
                  <>Reading {prProgress.done} of {prProgress.total} <CircularProgress size={10} sx={{ mx: 0.5 }} /></>
                )}
                {shownPrStats.notLoaded > 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => loadPrTests(view.features.flatMap(f => f.pullRequests || []))}
                    sx={{ py: 0, ml: 0.5, fontSize: '0.72rem', '@media print': { display: 'none' } }}
                  >
                    Load {shownPrStats.notLoaded} PR{shownPrStats.notLoaded === 1 ? '' : 's'}
                  </Button>
                )}
                {shownPrStats.notLoaded > 0 && prProgress.done >= prProgress.total && (
                  <Box component="span"> — opening a feature&apos;s Tests &amp; PRs loads just its PRs.</Box>
                )}
                {prProgress.error && <Box component="span" sx={{ color: COLORS.red }}> Some PRs could not be read: {prProgress.error}</Box>}
              </>
            )}
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.5 }}>
            <strong>Linked tests</strong> = Test Cases that are children of, or linked (Tested By / Related) to, the Feature
            or any PBI, Bug or Task below it. <strong>Completion</strong> = child PBIs/Bugs in Done / Completed / Resolved /
            Closed / Finished ÷ child PBIs/Bugs not Removed. <strong>Scenario coverage</strong> = one scenario per acceptance
            criterion of the feature and its PBIs/Bugs (the title when a PBI/Bug has none); covered when a linked test
            case matches it by title and steps (TF-IDF ≥ 0.3 — hover a cell to see the uncovered scenarios).{' '}
            <strong>New tests</strong> = linked tests created on or after the Feature/Epic was created.{' '}
            <strong>Automation status</strong> is the ADO test-case field. <strong>In repo</strong> checks Automated-UI /
            Automated-API cases: a new case must appear under e2e/releases, an older case anywhere in the repo;
            a manual case whose ID is in the repo is flagged as ID found, ADO manual.{' '}
            <strong>Outcome</strong> comes from the test plans, searching every suite under the given parent suite (failed
            beats passed if a case appears more than once); <strong>Not found in TP</strong> = the case is in none of them.{' '}
            <strong>PR unit tests</strong> use the GitHub PRs linked to the feature or anything below it.{' '}
            <strong>Suite results</strong> = the unit-test check runs on each PR&apos;s latest commit.{' '}
            <strong>New tests</strong> = test methods the PR adds (Java/Kotlin @Test-style, JS/TS it()/test(), Python test_*);
            each is looked up in that PR&apos;s own test reports: <strong>ran</strong> (passed/failed/skipped), <strong>not run</strong> (reports
            exist but the test is not in them), or <strong>can&apos;t verify</strong> (the reports are gone — GitHub deletes them a short time after the run).
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderTestPlanInputs = () => (
    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={2} sx={{ '@media print': { display: 'none' } }}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mr: 1 }}>Test plans</Typography>
      {planInputs.map((plan, index) => (
        <Box key={index} display="flex" gap={0.5} alignItems="center" sx={{ mr: 1 }}>
          <TextField
            size="small"
            label={`Plan ${index + 1} ID`}
            value={plan.planId}
            onChange={(e) => updatePlanInput(index, 'planId', e.target.value)}
            inputProps={{ inputMode: 'numeric' }}
            sx={{ width: 130 }}
          />
          <TextField
            size="small"
            label="Parent suite ID"
            value={plan.suiteId}
            onChange={(e) => updatePlanInput(index, 'suiteId', e.target.value)}
            inputProps={{ inputMode: 'numeric' }}
            sx={{ width: 140 }}
          />
        </Box>
      ))}
      <Button variant="outlined" size="small" onClick={applyPlans} disabled={loading}>Apply</Button>
      <Tooltip title={canSave ? 'Use these test plans for everyone viewing this release' : 'Log in as an admin, dev manager or QA manager to save'}>
        <span>
          <Button variant="contained" size="small" onClick={savePlans} disabled={loading || savingPlans}>
            {savingPlans ? 'Saving…' : 'Save for release'}
          </Button>
        </span>
      </Tooltip>
    </Box>
  );

  // Compact PR unit-test cell, shared by the summary and the feature table
  const renderPrCell = (stats, minWidth = 200, onLoad = null) => {
    if (!report.pullRequestsAvailable) {
      return <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', minWidth }}>Needs ADO</Typography>;
    }
    if (stats.prs === 0) {
      return <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', minWidth }}>No linked PRs</Typography>;
    }
    const { newTests } = stats;
    const loadedPrs = stats.prs - stats.unresolved - stats.pending - stats.notLoaded;
    if (loadedPrs === 0 && stats.pending === 0) {
      return (
        <Box sx={{ minWidth }}>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            {stats.prs} PR{stats.prs === 1 ? '' : 's'} · not loaded
          </Typography>
          {onLoad && (
            <Link component="button" onClick={onLoad} sx={{ fontSize: '0.72rem', '@media print': { display: 'none' } }}>Load</Link>
          )}
        </Box>
      );
    }
    return (
      <Box sx={{ minWidth }}>
        {newTests.total > 0 ? (
          <ProgressCell
            num={newTests.passed}
            den={newTests.total}
            caption={<>{newTests.total} new test{newTests.total === 1 ? '' : 's'}: <NewTestCaption stats={newTests} /></>}
            minWidth={minWidth}
          />
        ) : stats.pending === 0 ? (
          <Typography sx={{ fontSize: '0.75rem', color: COLORS.orange }}>No new tests in PRs</Typography>
        ) : null}
        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.25 }}>
          {stats.prs} PR{stats.prs === 1 ? '' : 's'} · {stats.withTestChanges} with test changes
          {stats.failingSuitePrs > 0 && (
            <Box component="span" sx={{ color: COLORS.red }}> · {stats.failingSuitePrs} with failing suites</Box>
          )}
          {stats.pending > 0 && <Box component="span"> · loading {stats.pending}…</Box>}
          {stats.notLoaded > 0 && <Box component="span"> · {stats.notLoaded} not loaded</Box>}
          {stats.errors > 0 && <Box component="span" sx={{ color: COLORS.red }}> · {stats.errors} unreadable</Box>}
          {stats.unresolved > 0 && <Box component="span"> · {stats.unresolved} in unknown repos</Box>}
        </Typography>
      </Box>
    );
  };

  const renderSummaryRow = (row, isTotal = false) => (
    <TableRow key={row.areaPath} sx={isTotal ? { '& td': { fontWeight: 700, backgroundColor: '#f8fafc' } } : undefined}>
      <TableCell sx={cellSx}>{row.areaPath}</TableCell>
      <TableCell sx={cellSx} align="center">{row.features}</TableCell>
      <TableCell sx={cellSx}>
        <ProgressCell num={row.completion.done} den={row.completion.total} caption={`${row.completion.done} / ${row.completion.total} child items`} />
      </TableCell>
      <TableCell sx={cellSx} align="center">
        <Box sx={{ fontWeight: 700 }}>{row.linkedCount}</Box>
        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
          {row.newCount} new · {row.newAutomatedCount} automated
        </Typography>
      </TableCell>
      <TableCell sx={cellSx}>
        {report.testPlans?.available ? (
          <ProgressCell num={row.counts.passed} den={row.linkedCount} caption={<ExecutionCaption counts={row.counts} />} minWidth={220} />
        ) : (
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', minWidth: 220 }}>No test plan outcomes</Typography>
        )}
      </TableCell>
      <TableCell sx={cellSx}>
        <ProgressCell num={row.scenarios.covered} den={row.scenarios.total} caption={`${row.scenarios.covered} of ${row.scenarios.total} scenarios covered`} minWidth={180} />
      </TableCell>
      <TableCell sx={cellSx}>
        {renderPrCell(row.prStats, 240, () => loadPrTests(
          view.features.filter(f => isTotal || f.areaPath === row.areaPath).flatMap(f => f.pullRequests || [])
        ))}
      </TableCell>
    </TableRow>
  );

  const renderSummary = () => (
    <Box mb={3}>
      <Typography sx={{ fontWeight: 700, mb: 1 }}>Summary by area path</Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Area Path</TableCell>
              <TableCell sx={headCellSx} align="center">Features</TableCell>
              <TableCell sx={headCellSx}>Overall completion</TableCell>
              <TableCell sx={headCellSx} align="center">
                Tests linked
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 400 }}>new = created after feature</Typography>
              </TableCell>
              <TableCell sx={headCellSx}>Test execution (test plans)</TableCell>
              <TableCell sx={headCellSx}>
                Test coverage
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 400 }}>derived scenarios covered by linked tests</Typography>
              </TableCell>
              <TableCell sx={headCellSx}>
                PR unit tests
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 400 }}>new tests in linked PRs that ran and passed</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {view.summaryByArea.map(row => renderSummaryRow(row))}
            {renderSummaryRow(view.total, true)}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const sectionTitleSx = { fontWeight: 700, fontSize: '0.85rem', mb: 0.5 };
  const subHeadSx = { ...headCellSx, backgroundColor: 'transparent' };

  const renderLinkedTestsTable = (feature) => (
    <Box mb={2}>
      <Typography sx={sectionTitleSx}>Linked test cases ({feature.linkedTests.length})</Typography>
      {feature.linkedTests.length === 0 ? (
        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>No test cases are linked to this feature or the items below it.</Typography>
      ) : (
        <Table size="small" sx={{ backgroundColor: '#fff' }}>
          <TableHead>
            <TableRow>
              {['Test case', 'Title', 'Linked via', 'Automation status (ADO)', 'Created', 'New', 'In repo', 'Test plan outcome'].map(h => (
                <TableCell key={h} sx={subHeadSx}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {feature.linkedTests.map(test => (
              <TableRow key={test.id}>
                <TableCell sx={cellSx}>
                  <Link href={`${ADO_WORK_ITEM_URL}${test.id}`} target="_blank" rel="noopener noreferrer">#{test.id}</Link>
                </TableCell>
                <TableCell sx={cellSx}>{test.title}</TableCell>
                <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                  {test.linkedFrom ? (
                    <>
                      {test.linkedFrom.via} of {test.linkedFrom.fromType}{' '}
                      <Link href={`${ADO_WORK_ITEM_URL}${test.linkedFrom.from}`} target="_blank" rel="noopener noreferrer">#{test.linkedFrom.from}</Link>
                    </>
                  ) : '—'}
                </TableCell>
                <TableCell sx={cellSx}>{test.automationStatus || '—'}</TableCell>
                <TableCell sx={cellSx}>{test.createdDate ? new Date(test.createdDate).toISOString().slice(0, 10) : '—'}</TableCell>
                <TableCell sx={cellSx}>{test.isNew ? 'Yes' : 'No'}</TableCell>
                <TableCell sx={{ ...cellSx, color: REPO_CHECK_COLORS[test.repoCheck] }}>{repoCheckLabel(test)}</TableCell>
                <TableCell sx={{ ...cellSx, color: OUTCOME_COLUMNS.find(c => c.key === test.outcome)?.color }}>
                  {test.outcome ? OUTCOME_LABELS[test.outcome] : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );

  // Unit-test check runs on the PR's latest commit, summarised; every suite on hover
  const renderSuites = (pr) => {
    if (pr.suites.length === 0) return <Typography sx={{ fontSize: '0.75rem', color: COLORS.orange }}>No unit-test runs</Typography>;
    const failing = pr.suites.filter(s => s.conclusion === 'failure');
    const tests = pr.suites.reduce((n, s) => n + (s.counts?.tests || 0), 0);
    const failedTests = pr.suites.reduce((n, s) => n + (s.counts?.failed || 0), 0);
    const detail = (
      <Box>
        {pr.suites.map(s => (
          <Typography key={s.fullName} sx={{ fontSize: '0.7rem' }}>
            {s.conclusion === 'failure' ? '✗' : s.conclusion === 'success' ? '✓' : '•'} {s.name}
            {s.counts ? ` — ${s.counts.passed} passed, ${s.counts.failed} failed, ${s.counts.skipped} skipped` : ` — ${s.conclusion || s.status}`}
          </Typography>
        ))}
      </Box>
    );
    return (
      <Tooltip title={detail} placement="left" arrow>
        <Box>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: failing.length > 0 ? COLORS.red : COLORS.green }}>
            {failing.length > 0 ? `${failing.length} of ${pr.suites.length} suites failing` : `All ${pr.suites.length} suites passed`}
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
            {tests.toLocaleString()} tests{failedTests > 0 ? ` · ${failedTests} failed` : ''}
            {failing.length > 0 ? ` · ${failing.map(s => s.name).join(', ')}` : ''}
          </Typography>
        </Box>
      </Tooltip>
    );
  };

  const renderNewTests = (pr) => {
    if (pr.newTests.length === 0) {
      return (
        <Typography sx={{ fontSize: '0.75rem', color: pr.testFilesChanged > 0 ? 'text.secondary' : COLORS.orange }}>
          {pr.testFilesChanged > 0 ? `No new test methods (${pr.testFilesChanged} test file${pr.testFilesChanged === 1 ? '' : 's'} changed)` : 'No test changes'}
          {pr.newTestsUnreadable > 0 && ` · ${pr.newTestsUnreadable} file diff${pr.newTestsUnreadable === 1 ? '' : 's'} too large to read`}
        </Typography>
      );
    }
    const counts = pr.newTests.reduce((acc, t) => ({ ...acc, [t.status]: (acc[t.status] || 0) + 1 }), {});
    return (
      <Box>
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700 }}>
          {pr.newTests.length} new: <NewTestCaption stats={counts} />
        </Typography>
        {pr.resultsNote && <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{pr.resultsNote}</Typography>}
        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
          {pr.newTests.slice(0, MAX_NEW_TESTS_SHOWN).map(t => {
            const status = NEW_TEST_STATUSES.find(s => s.key === t.status);
            return (
              <Box component="li" key={`${t.file}#${t.name}`} sx={{ fontSize: '0.72rem', wordBreak: 'break-word' }}>
                <Box component="span" sx={{ color: status?.color, fontWeight: 700 }}>{status?.label}</Box>{' '}
                <Tooltip title={t.file}><span>{t.className.split(/[./]/).pop()}#{t.name}</span></Tooltip>
              </Box>
            );
          })}
        </Box>
        {pr.newTests.length > MAX_NEW_TESTS_SHOWN && (
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>… and {pr.newTests.length - MAX_NEW_TESTS_SHOWN} more</Typography>
        )}
      </Box>
    );
  };

  const renderPullRequestsTable = (feature) => {
    const prs = feature.pullRequests || [];
    return (
      <Box>
        <Typography sx={sectionTitleSx}>Pull requests &amp; unit tests ({prs.length})</Typography>
        {!report.pullRequestsAvailable ? (
          <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>PR links are only read from Azure DevOps, which could not be reached.</Typography>
        ) : prs.length === 0 ? (
          <Typography sx={{ fontSize: '0.8rem', color: COLORS.orange }}>No GitHub PRs are linked to this feature or the items below it.</Typography>
        ) : (
          <Table size="small" sx={{ backgroundColor: '#fff' }}>
            <TableHead>
              <TableRow>
                {['Pull request', 'Linked from', 'State', 'Suite results (latest commit)', 'New tests in this PR'].map(h => (
                  <TableCell key={h} sx={subHeadSx}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {prs.map(link => {
                const pr = link.repo ? prTests[prKey(link)] : null;
                return (
                  <TableRow key={`${link.repo || link.repoId}#${link.number}`}>
                    <TableCell sx={{ ...cellSx, maxWidth: 320 }}>
                      {pr?.url ? (
                        <Link href={pr.url} target="_blank" rel="noopener noreferrer">{link.repo.split('/').pop()}#{link.number}</Link>
                      ) : `${link.repo ? link.repo.split('/').pop() : 'Unknown repo'}#${link.number}`}
                      {pr?.title && <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{pr.title}</Typography>}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                      {link.fromType}{' '}
                      <Link href={`${ADO_WORK_ITEM_URL}${link.from}`} target="_blank" rel="noopener noreferrer">#{link.from}</Link>
                    </TableCell>
                    {!link.repo ? (
                      <TableCell sx={{ ...cellSx, color: 'text.secondary' }} colSpan={3}>
                        Repo {link.repoId} is not in the portal&apos;s repo list, so the PR cannot be read.
                      </TableCell>
                    ) : !pr ? (
                      <TableCell sx={{ ...cellSx, color: 'text.secondary' }} colSpan={3}>Loading…</TableCell>
                    ) : pr.error ? (
                      <TableCell sx={{ ...cellSx, color: COLORS.red }} colSpan={3}>Could not read the PR: {pr.error}</TableCell>
                    ) : (
                      <>
                        <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                          {pr.state === 'merged' ? `Merged ${pr.mergedAt ? new Date(pr.mergedAt).toISOString().slice(0, 10) : ''}` : pr.state === 'open' ? 'Open' : 'Closed (not merged)'}
                          {pr.baseRef && <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>into {pr.baseRef}</Typography>}
                        </TableCell>
                        <TableCell sx={{ ...cellSx, minWidth: 180 }}>{renderSuites(pr)}</TableCell>
                        <TableCell sx={{ ...cellSx, minWidth: 320 }}>{renderNewTests(pr)}</TableCell>
                      </>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Box>
    );
  };

  const DETAIL_COLUMN_COUNT = 12;

  const renderFeatureDetail = (feature) => (
    <TableRow>
      <TableCell colSpan={DETAIL_COLUMN_COUNT} sx={{ backgroundColor: '#f8fafc', py: 1.5, borderLeft: `3px solid #2563eb` }}>
        {renderLinkedTestsTable(feature)}
        {renderPullRequestsTable(feature)}
      </TableCell>
    </TableRow>
  );

  // `feature` carries unsaved edits; `original` is the saved row the edits are compared against
  const renderFeatureRow = (feature) => {
    const original = report.features.find(f => f.id === feature.id);
    const dirty = !!pendingChanges[feature.id];
    const expanded = expandedTests.has(feature.id);
    const prStats = prStatsOf(feature.pullRequests || [], prTests, requestedPrs.current);
    const uncoveredTooltip = feature.scenarios.uncoveredCount > 0 ? (
      <Box>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, mb: 0.5 }}>
          Uncovered scenarios ({feature.scenarios.uncoveredCount})
        </Typography>
        {feature.scenarios.uncovered.map((s, i) => (
          <Typography key={i} sx={{ fontSize: '0.7rem' }}>• {s}</Typography>
        ))}
        {feature.scenarios.uncoveredCount > feature.scenarios.uncovered.length && (
          <Typography sx={{ fontSize: '0.7rem' }}>
            … and {feature.scenarios.uncoveredCount - feature.scenarios.uncovered.length} more
          </Typography>
        )}
      </Box>
    ) : '';

    return (
      <Fragment key={feature.id}>
        <TableRow hover sx={expanded ? { '& > td': { borderBottom: 'none' } } : undefined}>
          <TableCell sx={cellSx}>{feature.areaPath}</TableCell>
          <TableCell sx={{ ...cellSx, minWidth: 180 }}>
            <Link href={`${ADO_WORK_ITEM_URL}${feature.id}`} target="_blank" rel="noopener noreferrer" underline="hover">
              {feature.workItemType === 'Epic' ? '👑' : '🧩'} {feature.title}
            </Link>
            <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}> #{feature.id} ↗</Box>
            <Box sx={{ '@media print': { display: 'none' } }}>
              <Link component="button" onClick={() => toggleTests(feature)} sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {expanded ? 'Hide details ▴' : 'Tests & PRs ▾'}
              </Link>
            </Box>
          </TableCell>
          <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
            {feature.createdDate ? new Date(feature.createdDate).toISOString().slice(0, 10) : '—'}
          </TableCell>
          <TableCell sx={cellSx}>
            <ProgressCell num={feature.completion.done} den={feature.completion.total} caption={`${feature.completion.done} / ${feature.completion.total} items`} minWidth={110} />
          </TableCell>
          <TableCell sx={cellSx}>
            {feature.scenarios.total > 0 ? (
              <Tooltip title={uncoveredTooltip} placement="right" arrow>
                <Box>
                  <ProgressCell num={feature.scenarios.covered} den={feature.scenarios.total} caption={`${feature.scenarios.covered} / ${feature.scenarios.total} scenarios`} minWidth={110} />
                </Box>
              </Tooltip>
            ) : (
              <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', textAlign: 'center' }}>n/a</Typography>
            )}
          </TableCell>
          <TableCell sx={cellSx} align="center">
            <Box sx={{ fontWeight: 700 }}>{feature.linkedCount}</Box>
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>{feature.newCount} new</Typography>
          </TableCell>
          <TableCell sx={{ ...cellSx, minWidth: 120 }}><CountChips counts={feature.counts} columns={AUTOMATION_COLUMNS} /></TableCell>
          <TableCell sx={{ ...cellSx, minWidth: 110 }}><CountChips counts={feature.counts} columns={REPO_COLUMNS} /></TableCell>
          <TableCell sx={cellSx}>
            {report.testPlans?.available && feature.linkedCount > 0 ? (
              <ProgressCell num={feature.counts.passed} den={feature.linkedCount} caption={<ExecutionCaption counts={feature.counts} />} minWidth={150} />
            ) : (
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>—</Typography>
            )}
          </TableCell>
          <TableCell sx={cellSx}>{renderPrCell(prStats, 170, () => loadPrTests(feature.pullRequests || []))}</TableCell>
          <TableCell sx={{ ...cellSx, backgroundColor: dirty ? '#fef9c3' : '#e5e7eb', minWidth: 100 }} align="center">
            <Select
              value={feature.decision}
              onChange={(e) => editFeature(original, 'decision', e.target.value)}
              displayEmpty
              size="small"
              fullWidth
              sx={{
                fontSize: '0.8rem',
                fontWeight: 700,
                backgroundColor: '#fff',
                color: DECISION_COLORS[feature.decision] || 'text.secondary'
              }}
              renderValue={(v) => v || '— choose —'}
            >
              <MenuItem value="">— choose —</MenuItem>
              <MenuItem value="Go" sx={{ color: COLORS.green, fontWeight: 700 }}>Go</MenuItem>
              <MenuItem value="Risk" sx={{ color: COLORS.orange, fontWeight: 700 }}>Risk</MenuItem>
              <MenuItem value="No-Go" sx={{ color: COLORS.red, fontWeight: 700 }}>No-Go</MenuItem>
            </Select>
          </TableCell>
          <TableCell sx={{ ...cellSx, minWidth: 180, backgroundColor: dirty ? '#fef9c3' : undefined }}>
            <TextField
              value={feature.comment}
              onChange={(e) => editFeature(original, 'comment', e.target.value)}
              placeholder="Add comment"
              size="small"
              multiline
              minRows={2}
              fullWidth
              InputProps={{ sx: { fontSize: '0.8rem', backgroundColor: '#fff' } }}
            />
          </TableCell>
        </TableRow>
        {expanded && renderFeatureDetail(feature)}
      </Fragment>
    );
  };

  const renderDetail = () => {
    const { total } = view;
    return (
      <Box>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1} sx={{ '@media print': { display: 'none' } }}>
          <Box>
            <Typography sx={{ fontWeight: 700 }}>Detail by feature</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
              Open <strong>Tests &amp; PRs</strong> on a feature to see its linked test cases and the unit tests in its PRs.
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {pendingCount > 0 && (
              <>
                <Typography sx={{ fontSize: '0.8rem', color: COLORS.orange, fontWeight: 600 }}>
                  {pendingCount} unsaved change{pendingCount === 1 ? '' : 's'}
                </Typography>
                <Button size="small" onClick={() => setPendingChanges({})} disabled={saving}>Discard</Button>
              </>
            )}
            <Tooltip title={canSave ? '' : 'Log in as an admin, dev manager or QA manager to save'}>
              <span>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                  onClick={saveChanges}
                  disabled={pendingCount === 0 || saving}
                >
                  Save
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Area Path</TableCell>
                <TableCell sx={headCellSx}>Feature</TableCell>
                <TableCell sx={headCellSx}>Created</TableCell>
                <TableCell sx={headCellSx}>
                  Completion
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 400 }}>done ÷ child PBIs/Bugs</Typography>
                </TableCell>
                <TableCell sx={headCellSx}>
                  Scenario coverage
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 400 }}>by linked tests</Typography>
                </TableCell>
                <TableCell sx={headCellSx} align="center">Linked tests</TableCell>
                <TableCell sx={{ ...headCellSx, borderBottom: '3px solid #2563eb' }}>
                  Automation (ADO)
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 400 }}>linked tests by status</Typography>
                </TableCell>
                <TableCell sx={{ ...headCellSx, borderBottom: '3px solid #16a34a' }}>
                  In repo
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 400 }}>new: e2e/releases · old: anywhere</Typography>
                </TableCell>
                <TableCell sx={{ ...headCellSx, borderBottom: '3px solid #d97706' }}>Test plan outcome</TableCell>
                <TableCell sx={{ ...headCellSx, borderBottom: '3px solid #7c3aed' }}>
                  PR unit tests
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 400 }}>new tests ran &amp; passed</Typography>
                </TableCell>
                <TableCell sx={{ ...headCellSx, backgroundColor: '#e5e7eb' }} align="center">
                  Decision for {report.fixVersion}
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 400 }}>Go / Risk / No-Go</Typography>
                </TableCell>
                <TableCell sx={{ ...headCellSx, backgroundColor: '#dbe7ea' }}>Comment</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {view.features.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={DETAIL_COLUMN_COUNT} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {report.features.length === 0 ? 'No Features or Epics found for this release.' : 'No features match the selected area paths.'}
                  </TableCell>
                </TableRow>
              ) : view.features.map(renderFeatureRow)}
              <TableRow sx={{ '& td': { fontWeight: 700, backgroundColor: '#f8fafc' } }}>
                <TableCell sx={cellSx} colSpan={3}>Total</TableCell>
                <TableCell sx={cellSx}>
                  <ProgressCell num={total.completion.done} den={total.completion.total} caption={`${total.completion.done} / ${total.completion.total} items`} minWidth={110} />
                </TableCell>
                <TableCell sx={cellSx}>
                  <ProgressCell num={total.scenarios.covered} den={total.scenarios.total} caption={`${total.scenarios.covered} / ${total.scenarios.total} scenarios`} minWidth={110} />
                </TableCell>
                <TableCell sx={cellSx} align="center">
                  {total.linkedCount}
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{total.newCount} new</Typography>
                </TableCell>
                <TableCell sx={cellSx}><CountChips counts={total.counts} columns={AUTOMATION_COLUMNS} /></TableCell>
                <TableCell sx={cellSx}><CountChips counts={total.counts} columns={REPO_COLUMNS} /></TableCell>
                <TableCell sx={cellSx}>
                  {report.testPlans?.available ? (
                    <ProgressCell num={total.counts.passed} den={total.linkedCount} caption={<ExecutionCaption counts={total.counts} />} minWidth={150} />
                  ) : '—'}
                </TableCell>
                <TableCell sx={cellSx}>{renderPrCell(total.prStats, 170)}</TableCell>
                <TableCell sx={{ ...cellSx, fontSize: '0.75rem' }} colSpan={2}>
                  <Box component="span" sx={{ color: COLORS.green }}>Go {total.decisions.Go}</Box>{' · '}
                  <Box component="span" sx={{ color: COLORS.orange }}>Risk {total.decisions.Risk}</Box>{' · '}
                  <Box component="span" sx={{ color: COLORS.red }}>No-Go {total.decisions['No-Go']}</Box>{' · '}
                  <Box component="span" sx={{ color: 'text.secondary' }}>Not decided {total.decisions.undecided}</Box>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mb={2} sx={{ '@media print': { display: 'none' } }}>
        <Autocomplete
          size="small"
          sx={{ width: 260 }}
          options={versionOptions}
          value={selectedFixVersion || null}
          onChange={(e, value) => {
            if (!value) return;
            if (pendingCount > 0 && !window.confirm('You have unsaved decision changes. Switch release and discard them?')) return;
            setSelectedFixVersion(value);
          }}
          autoHighlight
          disableClearable
          renderInput={(params) => <TextField {...params} label="Release (type to search)" />}
        />
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={() => fetchReport(true)}
          disabled={loading || !selectedFixVersion}
        >
          Refresh (incl. repo scan)
        </Button>
        <Button variant="outlined" startIcon={<PrintIcon />} onClick={printReport} disabled={!report}>
          Print / Save as PDF
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!selectedFixVersion ? (
        <Typography variant="body2" color="text.secondary" align="center">
          Select a release to view its feature status.
        </Typography>
      ) : loading && !report ? (
        <Loading message="Building release status (first load scans the e2e repo and can take a minute)..." />
      ) : report ? (
        <Paper ref={reportRef} elevation={0} sx={{ p: 2, opacity: loading ? 0.6 : 1 }}>
          {renderTestPlanInputs()}
          {renderHeader()}
          <Autocomplete
            multiple
            size="small"
            options={view.areaOptions}
            value={areaFilter}
            onChange={(e, value) => setAreaFilter(value)}
            disableCloseOnSelect
            filterSelectedOptions
            sx={{ mb: 2, maxWidth: 700, '@media print': { display: areaFilter.length > 0 ? 'block' : 'none' } }}
            renderInput={(params) => (
              <TextField {...params} label="Area Path" placeholder={areaFilter.length === 0 ? 'All area paths — type to filter' : ''} />
            )}
          />
          {renderSummary()}
          {renderDetail()}
        </Paper>
      ) : null}

      <Snackbar open={!!snackbar} autoHideDuration={5000} onClose={() => setSnackbar('')} message={snackbar} />
    </Box>
  );
};

export default ReleaseStatusTab;
